import { GateOutMismatchMode, TemperatureUnit, TenantDto, TerminalType } from '@admin/app/api'
import { FormProps } from '@admin/app/models'
import { ControlledNumberField, ControlledSelect } from '@admin/components/form-controls'
import { ControlledCheckbox } from '@admin/components/form-controls/ControlledCheckbox'
import { ControlledTextField } from '@admin/components/form-controls/ControlledTextField'
import useFormWithSchema from '@admin/hooks/use-form-with-schema.hook'
import { SingleLocationCodeAutocompleteProps } from '@admin/pages/TenantConfiguration/components/form/tenant-config.interface'
import { TenantLocationCodeField } from '@admin/pages/TenantConfiguration/components/form/TenantLocationCodeField'
import { TimezonesStore } from '@admin/stores/timezones.store'
import { Box, FormGroup, Grid, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment } from '@tom-ui/ui'
import { uniq } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Control } from 'react-hook-form'
import { TenantFormMapper } from './tenant-form.mapper'
import { TenantFormProfile, defaultValues } from './tenant-form.profile'
import { schema } from './tenant-form.scheme'

interface Props extends FormProps {
  tenant?: TenantDto
  timezonesStore: TimezonesStore
  onSubmit: (formValues: TenantFormProfile) => Promise<void> | void
  SingleLocationCodeAutoComplete: React.ComponentType<SingleLocationCodeAutocompleteProps>
}

export const TenantForm = observer(
  ({ id, tenant, timezonesStore, onSubmit, SingleLocationCodeAutoComplete }: Props) => {
    const { handleSubmit, formState, control, setValue, getValues, reset } = useFormWithSchema<
      Pick<TenantFormProfile, 'id' | 'name' | 'unloCodes' | 'timezone'>
    >(schema(), defaultValues)
    // Cast to full form type — schema only validates required fields, but control is used
    // for all form fields via defaultValues
    const formControl = control as unknown as Control<TenantFormProfile>

    const [formErrors, setFormErrors] = useState({
      hasDuplicateLocationCodes: false,
      isRequiredLocationCode: false,
    })

    const { t } = useTranslate()

    useEffect(() => {
      if (tenant) {
        reset(TenantFormMapper.mapDtoToFormValues(tenant))
      }
    }, [reset, tenant])

    const temperatureUnitOptions = useMemo(
      () => [
        { label: t(TemperatureUnit.Celsius), value: TemperatureUnit.Celsius },
        { label: t(TemperatureUnit.Fahrenheit), value: TemperatureUnit.Fahrenheit },
      ],
      [],
    )

    const gateOutMismatchModeOptions = useMemo(
      () => [
        { label: t('gateOutMismatchModeWarn', 'Warn'), value: GateOutMismatchMode.Warn },
        { label: t('gateOutMismatchModeBlock', 'Block'), value: GateOutMismatchMode.Block },
      ],
      [],
    )

    const operatorViewSettingsDescription = (type: TerminalType) => {
      switch (type) {
        case TerminalType.Hig:
          return t('default', 'Default')

        case TerminalType.Inland:
          return t('inland', 'Inland')
        default:
          return t('unknown', 'Unknown')
      }
    }

    const handleSubmitForm = async (formValues: TenantFormProfile) => {
      const locationCodes = getValues('unloCodes')
      const uniqueLocationCodes = uniq(locationCodes?.map(lc => lc.name))

      if (!locationCodes || locationCodes.length === 0) {
        return setFormErrors({ ...formErrors, isRequiredLocationCode: true })
      }

      if (locationCodes?.length !== uniqueLocationCodes.length) {
        return setFormErrors({ ...formErrors, hasDuplicateLocationCodes: true })
      }

      setFormErrors({ hasDuplicateLocationCodes: false, isRequiredLocationCode: false })

      await onSubmit(formValues)
    }

    return (
      <Box
        id={id}
        component='form'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <FormGroup sx={{ gap: theme => theme.customSpacing.l, paddingBottom: 2 }}>
          <Grid container spacing={2}>
            <Grid size='grow'>
              <ControlledTextField<TenantFormProfile>
                name='name'
                formState={formState}
                control={formControl}
                label={t('name', 'Name')}
                fullWidth
              />
            </Grid>
            <Grid size='grow'>
              <ControlledSelect<TenantFormProfile>
                name='timezone'
                formState={formState}
                control={formControl}
                label={t('timeZone', 'Time Zone')}
                options={timezonesStore.timezones.map(tz => ({
                  label: tz.name,
                  value: tz.id,
                }))}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size='grow'>
              <ControlledSelect<TenantFormProfile>
                name='culture'
                formState={formState}
                control={formControl}
                label={t('culture', 'Culture')}
                options={timezonesStore.cultures.map(c => ({
                  label: c.name,
                  value: c.id,
                }))}
              />
            </Grid>
            <Grid size='grow'>
              <ControlledSelect<TenantFormProfile>
                name='temperatureUnit'
                formState={formState}
                control={formControl}
                label={t('temperatureUnit', 'Temperature unit')}
                options={temperatureUnitOptions}
              />
            </Grid>
          </Grid>
          <ControlledTextField<TenantFormProfile>
            name='address'
            formState={formState}
            control={formControl}
            label={t('address', 'Address')}
            fullWidth
          />
          <ControlledNumberField<TenantFormProfile>
            name='truckArrivalWindowDuration'
            formState={formState}
            control={formControl}
            label={t('truckArrivalWindowDuration', 'Truck arrival window duration')}
            fullWidth
            endAdornment={
              <CustomInputAdornment position='end'>{t('hour', 'hour')}</CustomInputAdornment>
            }
            integerOnly
          />
          <TenantLocationCodeField
            tenant={tenant}
            formState={formState}
            control={formControl}
            setValue={setValue}
            getValues={getValues}
            formErrors={formErrors}
            SingleLocationCodeAutoComplete={SingleLocationCodeAutoComplete}
          />

          <Stack>
            <Typography variant='subtitle2'>{t('configuration', 'Configuration')}</Typography>
            <ControlledCheckbox<TenantFormProfile>
              name={'hasAutoGeneratedCraneSplit'}
              formState={formState}
              control={formControl}
              label={t('hasAutoGeneratedCraneSplit', 'Has auto generated crane split')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipCraneSplit'}
              formState={formState}
              control={formControl}
              label={t('skipCraneSplit', 'Skip crane split')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipVesselTally'}
              formState={formState}
              control={formControl}
              label={t('skipVesselTally', 'Skip vessel tally')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipRailTally'}
              formState={formState}
              control={formControl}
              label={t('skipRailTally', 'Skip rail tally')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipRailTallyLoadPreparation'}
              formState={formState}
              control={formControl}
              label={t('skipRailTallyLoadPreparation', 'Skip rail tally load preparation')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipGateOut'}
              formState={formState}
              control={formControl}
              label={t('skipGateOut', 'Skip gate out')}
            />
            <ControlledSelect<TenantFormProfile>
              name='gateOutMismatchMode'
              formState={formState}
              control={formControl}
              label={t('gateOutMismatchMode', 'Gate-out at wrong exit gate')}
              options={gateOutMismatchModeOptions}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipTruckAppointment'}
              formState={formState}
              control={formControl}
              label={t('skipTruckVisit', 'Skip truck visit')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipReleaseOrder'}
              formState={formState}
              control={formControl}
              label={t('skipReleaseOrder', 'Skip release order')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipVesselVisitReadyForOperation'}
              formState={formState}
              control={formControl}
              label={t('skipVesselVisitReadyForOperation', 'Skip vessel visit ready for operation')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipYardPlanning'}
              formState={formState}
              control={formControl}
              label={t('skipYardPlanning', 'Skip yard planning')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipBerthTimestamp'}
              formState={formState}
              control={formControl}
              label={t('skipBerthingTimestamp', 'Skip berthing timestamp')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name='bayRowUniqueIdentificationMode'
              formState={formState}
              control={formControl}
              label={t('bayRowUniqueIdentificationMode', 'Bay row unique identification mode')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'isInlandJobCreationFlow'}
              formState={formState}
              control={formControl}
              label={t('isInlandJobCreationFlow', 'Is Inland job creation flow')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'showUnifiedOperatorPage'}
              formState={formState}
              control={formControl}
              label={t('showUnifiedOperatorPage', 'Show unified operator page')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'hasGeneralCargo'}
              formState={formState}
              control={formControl}
              label={t('hasGeneralCargo', 'Has general cargo')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'hasDamageCatalogue'}
              formState={formState}
              control={formControl}
              label={t('hasDamageCatalogue', 'Has damage catalogue')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'allowGateInWhenUnitNotOnTerminal'}
              formState={formState}
              control={formControl}
              label={t(
                'allowGateInWhenUnitNotOnTerminal',
                'Allow gate in when unit not on terminal',
              )}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'allowDirectDelivery'}
              formState={formState}
              control={formControl}
              label={t('allowDirectDelivery', 'Allow direct delivery')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'allowOperatorToSelectRailcar'}
              formState={formState}
              control={formControl}
              label={t('allowOperatorToSelectRailcar', 'Allow operator to select railcar')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'autoTriggerVesselWorkQueues'}
              formState={formState}
              control={formControl}
              label={t('autoTriggerVesselWorkQueues', 'Stop trigger WQ automatically')}
            />
            <ControlledCheckbox<TenantFormProfile>
              name={'skipGangSelection'}
              formState={formState}
              control={formControl}
              label={t('skipGangSelection', 'Skip gang selection')}
            />
            <ControlledSelect<TenantFormProfile>
              name='operatorViewConfigs'
              formState={formState}
              control={formControl}
              label={t('operatorView', 'Operator view settings')}
              options={[TerminalType.Hig, TerminalType.Inland].map(c => ({
                label: operatorViewSettingsDescription(c),
                value: c,
              }))}
            />
          </Stack>
        </FormGroup>
      </Box>
    )
  },
)
